import ErrorHandler from "../ErrorHandler";
import "../../css/common.css";
import styles from "./Table.module.css";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
  memo,
  useRef,
} from "react";

function Table({
  data,
  columns,
  loading,
  loading_rows = 5,
  loading_cols = columns?.length || 0,
  no_data_msg = "No Data Found",
  td_text_position = "start",
  th_text_position = "start",
  tfoot = null,
  flag,
  maxTableHeight = "100%",
  enablePagination = false,
  defaultRowsPerPage = 50,
  rowsPerPageOptions = [25, 50, 100, 200],
  rowsPerPageAsInput = false,
  minTableHeight,
  tableRef = null,
  serverSidePagination = false,
  totalCount = 0,
  totalPages: totalPagesProp = null,
  onPageChange,
  page = 1,
  rowsPerPage: rowsPerPageProp,
  onRowsPerPageChange,
  hideFirstLastButtons = false,
  hidePaginationInfo = false,
  showTopScrollbar = false,
  theadClassName = "",
  theadStyle = null,
  header_rows = null,
  getRowClassName = null,
  onRowClick = null,
}) {
  const innerTableRef = useRef(null);
  const topBarRef = useRef(null);
  const phantomRef = useRef(null);
  const isSyncing = useRef(false);
  const stickyHeaderRefs = useRef({});
  const headerRowRefs = useRef([]);

  const [filteredData, setFilteredData] = useState(data);
  columns = columns.filter((col) => col);
  loading_cols = columns?.length || 0;
  const [currentPage, setCurrentPage] = useState(page || 1);
  // `rowsPerPage` is controlled when the caller passes it (needed for
  // serverSidePagination, so a page-size change can be reflected back into
  // the request the parent sends) — otherwise it's plain internal state,
  // seeded once from `defaultRowsPerPage`, exactly as before.
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(defaultRowsPerPage);
  const rowsPerPage = rowsPerPageProp ?? internalRowsPerPage;

  // Multiple columns can opt into `sticky: true`. Each one needs a `left`
  // offset equal to the combined width of the sticky columns before it, so
  // they sit side-by-side pinned to the left edge instead of all stacking
  // at left:0 and overlapping. Column widths are content-driven (no fixed
  // width), so this can't be done in static CSS — measure each sticky
  // header cell after layout and derive cumulative offsets from that.
  const stickyColumnIndexes = columns.reduce((acc, column, index) => {
    if (column?.sticky) acc.push(index);
    return acc;
  }, []);
  // `columns` is a new array/object literal on effectively every render
  // (both from this component's own `columns.filter(...)` above and from
  // callers that inline their column defs), so it has no stable identity
  // to depend on. Derive a primitive string instead — stable across
  // renders whenever the actual sticky indexes are unchanged — so the
  // effect below only re-runs when sticky columns genuinely change,
  // rather than on every render (which previously caused an infinite
  // render loop: effect runs -> setStickyOffsets -> re-render -> new
  // `columns` array -> effect "changed" -> runs again, forever).
  const stickyColumnIndexesKey = stickyColumnIndexes.join(",");

  const [stickyOffsets, setStickyOffsets] = useState({});

  useLayoutEffect(() => {
    if (stickyColumnIndexes.length === 0) {
      setStickyOffsets({});
      return;
    }

    const recomputeStickyOffsets = () => {
      let cumulativeLeft = 0;
      const offsets = {};
      stickyColumnIndexes.forEach((index) => {
        offsets[index] = cumulativeLeft;
        const width =
          stickyHeaderRefs.current[index]?.getBoundingClientRect().width || 0;
        cumulativeLeft += width;
      });
      setStickyOffsets((prev) =>
        JSON.stringify(prev) === JSON.stringify(offsets) ? prev : offsets,
      );
    };

    recomputeStickyOffsets();

    // A ResizeObserver (rather than guessing which props/state affect
    // rendered cell width) catches every real cause of a width change:
    // pagination, sorting, filtering, column config, window resize, etc.
    const resizeObserver = new ResizeObserver(recomputeStickyOffsets);
    stickyColumnIndexes.forEach((index) => {
      const el = stickyHeaderRefs.current[index];
      if (el) resizeObserver.observe(el);
    });

    return () => resizeObserver.disconnect();
    // Deliberately keyed off the primitive string key, not the array (see
    // comment above stickyColumnIndexesKey) — this is intentional, not a
    // missed dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyColumnIndexesKey]);

  // A multi-row header_rows table has more than one <tr> in <thead>, and the
  // CSS gives every thead th `position: sticky; top: 0` -- correct for a
  // rowSpan=2 cell (it IS anchored at the very top), but wrong for a second
  // row's own cells (e.g. a spanning group's member columns): without an
  // offset they also stick to top:0 and paint over row one instead of
  // stacking beneath it. Row heights are content-driven (font size, wrapped
  // labels), so this can't be a static CSS value -- measure each header row
  // after layout, same approach as the sticky-column offsets above.
  const headerRowsKey = header_rows
    ? header_rows.map((row) => row.length).join(",")
    : "";
  const [headerRowTopOffsets, setHeaderRowTopOffsets] = useState([]);

  useLayoutEffect(() => {
    if (!header_rows || header_rows.length <= 1) {
      setHeaderRowTopOffsets([]);
      return;
    }

    const recomputeHeaderRowOffsets = () => {
      let cumulativeTop = 0;
      const offsets = [];
      header_rows.forEach((_row, index) => {
        offsets[index] = cumulativeTop;
        const height =
          headerRowRefs.current[index]?.getBoundingClientRect().height || 0;
        cumulativeTop += height;
      });
      setHeaderRowTopOffsets((prev) =>
        JSON.stringify(prev) === JSON.stringify(offsets) ? prev : offsets,
      );
    };

    recomputeHeaderRowOffsets();

    const resizeObserver = new ResizeObserver(recomputeHeaderRowOffsets);
    headerRowRefs.current.forEach((el) => {
      if (el) resizeObserver.observe(el);
    });

    return () => resizeObserver.disconnect();
    // Deliberately keyed off the primitive string key, not the header_rows
    // array itself (same reasoning as stickyColumnIndexesKey above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRowsKey]);

  useEffect(() => {
    setFilteredData(data);
    if (!serverSidePagination) setCurrentPage(1);
  }, [data, serverSidePagination]);

  useEffect(() => {
    if (serverSidePagination) setCurrentPage(page);
  }, [page, serverSidePagination]);

  const [sortOrder, setSortOrder] = useState({
    column: null,
    order: null,
  });

  useEffect(() => {
    if (loading) {
      setSortOrder({ column: null, order: null });
    }
  }, [loading]);

  const getDataFromDataKey = useCallback((row, data_key) => {
    try {
      if (!data_key || !row) return "";
      let keys = data_key?.split(".");
      let value = row;
      for (let i = 0; i < keys.length; i++) {
        value = value?.[keys[i]];
        if (value === undefined) break;
      }
      return value === 0 ? 0 : value || "";
    } catch (error) {
      return "";
    }
  }, []);

  const sortColumn = useCallback(() => {
    let sorted_data = structuredClone(data);

    if (sortOrder.order === "asc") {
      sorted_data = filteredData.toSorted((a, b) => {
        return getDataFromDataKey(a, sortOrder.column.data_key)
          ?.toString()
          .localeCompare(
            getDataFromDataKey(b, sortOrder.column.data_key),
            undefined,
            { numeric: true },
          );
      });
    }
    if (sortOrder.order === "desc") {
      sorted_data = filteredData.toSorted((a, b) => {
        return getDataFromDataKey(b, sortOrder.column.data_key)
          ?.toString()
          .localeCompare(
            getDataFromDataKey(a, sortOrder.column.data_key),
            undefined,
            { numeric: true },
          );
      });
    }
    return sorted_data;
  }, [sortOrder, filteredData, data, getDataFromDataKey]);

  const sortedData = useMemo(() => {
    let sorted_data = structuredClone(filteredData);
    sorted_data = sortColumn();

    if (loading) return sorted_data;
    if (!sortOrder?.column) return sorted_data;
    if (sorted_data?.length === 0) return sorted_data;

    return sorted_data;
  }, [sortOrder, loading, filteredData, data, sortColumn]);

  const totalRows = serverSidePagination
    ? totalCount || sortedData?.length || 0
    : sortedData?.length || 0;
  const totalPages =
    serverSidePagination && totalPagesProp != null
      ? totalPagesProp
      : Math.ceil(totalRows / rowsPerPage);

  const paginatedData = useMemo(() => {
    if (!enablePagination) return sortedData;
    if (serverSidePagination) return sortedData;

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedData?.slice(startIndex, endIndex) || [];
  }, [
    sortedData,
    currentPage,
    rowsPerPage,
    enablePagination,
    serverSidePagination,
  ]);
  
  // Columns flagged `group_span: true` merge consecutive rows that share the
  // same data_key value into one spanning cell (e.g. grouping asset rows by
  // State, or UBP rows by Bidder Info) instead of repeating the value on
  // every row. Computed from paginatedData so it always matches what's
  // actually on screen, including after a sort or filter.
  const groupSpans = useMemo(() => {
    const map = {};
    columns.forEach((column) => {
      if (!column?.group_span) return;
      const spans = {};
      const skip = new Set();
      const groupKey = column.group_key || column.data_key;
      const arr = paginatedData || [];
      let i = 0;
      while (i < arr.length) {
        const val = getDataFromDataKey(arr[i], groupKey);
        let j = i + 1;
        while (
          j < arr.length &&
          getDataFromDataKey(arr[j], groupKey) === val
        ) {
          skip.add(j);
          j++;
        }
        spans[i] = j - i;
        i = j;
      }
      map[column.data_key] = { spans, skip };
    });
    return map;
  }, [columns, paginatedData, getDataFromDataKey]);

  const handlePageChange = useCallback(
    (newPage) => {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (onPageChange) onPageChange(newPage);
    },
    [onPageChange],
  );

  const applyRowsPerPage = useCallback(
    (value) => {
      if (!value || value <= 0) return;
      setInternalRowsPerPage(value);
      setCurrentPage(1);
      onRowsPerPageChange?.(value);
      if (onPageChange) onPageChange(1);
    },
    [onRowsPerPageChange, onPageChange],
  );

  const handleRowsPerPageChange = useCallback(
    (e) => applyRowsPerPage(Number(e.target.value)),
    [applyRowsPerPage],
  );

  // Text-input variant of rows-per-page (rowsPerPageAsInput) needs its own
  // draft string state so the value can commit on blur/Enter rather than on
  // every keystroke (which would reset the page mid-typing).
  const [rowsPerPageInputValue, setRowsPerPageInputValue] = useState(
    String(rowsPerPage),
  );

  useEffect(() => {
    setRowsPerPageInputValue(String(rowsPerPage));
  }, [rowsPerPage]);

  const commitRowsPerPageInput = useCallback(() => {
    const parsed = parseInt(rowsPerPageInputValue, 10);
    if (!parsed || parsed <= 0) {
      setRowsPerPageInputValue(String(rowsPerPage));
      return;
    }
    setRowsPerPageInputValue(String(parsed));
    applyRowsPerPage(parsed);
  }, [rowsPerPageInputValue, rowsPerPage, applyRowsPerPage]);

  const getPageNumbers = useMemo(() => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  const handleSort = useCallback(
    (column) => {
      if (column?.sortable && !loading && filteredData?.length > 0) {
        setSortOrder((prev) => {
          if (prev?.column?.data_key === column?.data_key) {
            return {
              column: column,
              order:
                prev.order === "asc"
                  ? "desc"
                  : prev.order === "desc"
                    ? null
                    : "asc",
            };
          } else {
            return { column: column, order: "asc" };
          }
        });
      }
    },
    [loading, filteredData],
  );

  useEffect(() => {
    if (!showTopScrollbar) return;
    const tableDiv = innerTableRef.current;
    const topBar = topBarRef.current;
    const phantom = phantomRef.current;
    if (!tableDiv || !topBar || !phantom) return;

    let rafId = null;
    const syncPhantomWidth = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (innerTableRef.current && phantomRef.current) {
          phantomRef.current.style.width =
            innerTableRef.current.scrollWidth + "px";
        }
      });
    };
    syncPhantomWidth();

    const ro = new ResizeObserver(syncPhantomWidth);
    ro.observe(tableDiv);

    const onTopBarScroll = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      tableDiv.scrollLeft = topBar.scrollLeft;
      isSyncing.current = false;
    };
    const onTableScroll = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      topBar.scrollLeft = tableDiv.scrollLeft;
      isSyncing.current = false;
    };

    topBar.addEventListener("scroll", onTopBarScroll);
    tableDiv.addEventListener("scroll", onTableScroll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      topBar.removeEventListener("scroll", onTopBarScroll);
      tableDiv.removeEventListener("scroll", onTableScroll);
    };
  }, [showTopScrollbar]);

  try {
    return (
      <>
        {showTopScrollbar && (
          <div ref={topBarRef} className={styles.topbar} aria-hidden="true">
            <div ref={phantomRef} style={{ height: "1px" }} />
          </div>
        )}
        <div
          className={styles.tableShell}
          style={{
            maxHeight: maxTableHeight,
            minHeight: minTableHeight ? minTableHeight : "auto",
          }}
          ref={(node) => {
            innerTableRef.current = node;
            if (tableRef && typeof tableRef === "object") {
              tableRef.current = node;
            }
          }}
        >
          <table className={styles.table}>
            <thead>
              {header_rows ? (
                // Presentational only: cells here carry no click-to-sort, and this
                // path skips the sticky-column measurement that reads refs off the
                // auto row -- don't combine header_rows with `sticky` columns.
                header_rows.map((headerRow, headerRowIndex) => (
                  <tr
                    key={`thead_custom_row_${headerRowIndex}`}
                    ref={(el) => {
                      headerRowRefs.current[headerRowIndex] = el;
                    }}
                  >
                    {headerRow.map((cell, cellIndex) => (
                      <th
                        key={`thead_custom_${headerRowIndex}_${cellIndex}`}
                        colSpan={cell?.col_span}
                        rowSpan={cell?.row_span}
                        className={
                          "align-middle " +
                          (cell?.th_class || "") +
                          ` text-${cell?.th_text_position || th_text_position}` +
                          (theadClassName ? ` ${theadClassName}` : "")
                        }
                        style={{
                          ...(theadStyle ? { ...theadStyle, ...cell?.style } : cell?.style),
                          top: headerRowTopOffsets[headerRowIndex] ?? 0,
                        }}
                      >
                        {cell?.title}
                      </th>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                {columns.map((column, index) => {
                  const thAlignment = column?.th_text_position
                    ? column?.th_text_position
                    : index === 0
                      ? "center"
                      : th_text_position;
                  const isSortable = !!column?.sortable;
                  const isSortedHere =
                    sortOrder.column?.data_key === column.data_key;
                  return (
                    <th
                      key={`thead_${column?.data_key}_${index}`}
                      ref={
                        column?.sticky
                          ? (el) => {
                              stickyHeaderRefs.current[index] = el;
                            }
                          : undefined
                      }
                      rowSpan={column?.row_span}
                      colSpan={column?.col_span}
                      className={
                        (column?.reset_default_bootstrap_classes_thead
                          ? ""
                          : "align-middle ") +
                        (column?.th_class || "") +
                        ` text-${thAlignment}` +
                        (isSortable ? ` ${styles.isSortable}` : "") +
                        (theadClassName ? ` ${theadClassName}` : "") +
                        (column?.sticky ? ` ${styles.stickyCol}` : "")
                      }
                      style={{
                        ...(theadStyle
                          ? { ...theadStyle, ...column?.style }
                          : column?.style),
                        ...(column?.sticky
                          ? { left: stickyOffsets[index] ?? 0 }
                          : {}),
                      }}
                      onClick={() => handleSort(column)}
                    >
                      <span>{column?.title}</span>
                      {isSortedHere && sortOrder.order === "asc" ? (
                        <i className={`bi bi-arrow-up ${styles.sortIcon}`} />
                      ) : isSortedHere && sortOrder.order === "desc" ? (
                        <i className={`bi bi-arrow-down ${styles.sortIcon}`} />
                      ) : null}
                    </th>
                  );
                })}
                </tr>
              )}
            </thead>

            <tbody>
              {loading
                ? Array(loading_rows)
                    .fill(0)
                    .map((_, i) => (
                      <tr key={`${i}_table_loading_row`} className={styles.loadingRow}>
                        {Array(loading_cols)
                          .fill(0)
                          .map((_, j) => (
                            <td key={`${j}_table_loading_column`}>
                              <p className="placeholder-glow mb-0">
                                <span className="placeholder col-12"></span>
                              </p>
                            </td>
                          ))}
                      </tr>
                    ))
                : paginatedData?.map((row, row_index) => (
                    <tr
                      key={`data_row_${row_index}`}
                      className={
                        getRowClassName
                          ? getRowClassName(row, row_index) || undefined
                          : undefined
                      }
                      onClick={onRowClick ? () => onRowClick(row, row_index) : undefined}
                      style={onRowClick ? { cursor: "pointer" } : undefined}
                    >
                      {columns.map((column, column_index) => {
                        if (
                          column?.group_span &&
                          groupSpans[column.data_key]?.skip.has(row_index)
                        ) {
                          return null;
                        }
                        const tdAlignment = column?.td_text_position
                          ? column?.td_text_position
                          : column_index === 0
                            ? "center"
                            : td_text_position;
                        return (
                          <td
                            className={
                              (column?.reset_default_bootstrap_classes_td
                                ? " "
                                : "align-middle ") +
                              (column?.td_class || "") +
                              ` text-${tdAlignment}` +
                              (column?.sticky ? ` ${styles.stickyCol}` : "")
                            }
                            colSpan={column?.td_col_span}
                            rowSpan={
                              column?.group_span
                                ? groupSpans[column.data_key]?.spans[
                                    row_index
                                  ] || 1
                                : column?.td_row_span
                            }
                            style={{
                              ...column?.td_style,
                              ...(column?.sticky
                                ? { left: stickyOffsets[column_index] ?? 0 }
                                : {}),
                            }}
                            key={`data_row_${row_index}_column_${column_index}`}
                          >
                            {column?.render
                              ? column?.render(row, row_index, column_index)
                              : getDataFromDataKey(row, column?.data_key)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
            </tbody>

            {!loading && sortedData?.length > 0 && flag === "OverView" && (
              <tfoot>
                <tr>
                  <th colSpan={7}>Total</th>

                  <th colSpan={1} className="align-middle text-start">
                    {(() => {
                      const GrandTotal_asset_Count = sortedData.reduce(
                        (acc, item) => {
                          const assetCount = Number(item?.asset_count) || 0;
                          return acc + assetCount;
                        },
                        0,
                      );

                      const GrandTotal_asset_bidded_count = sortedData.reduce(
                        (acc, item) => {
                          const biddersCount =
                            Number(item?.bidded_asset_count) || 0;
                          return acc + biddersCount;
                        },
                        0,
                      );

                      const participating_percentage =
                        GrandTotal_asset_Count > 0
                          ? (
                              (GrandTotal_asset_bidded_count /
                                GrandTotal_asset_Count) *
                              100
                            ).toFixed(2)
                          : 0;
                      return (
                        <>
                          <span>
                            {" "}
                            {GrandTotal_asset_bidded_count} /{" "}
                            {GrandTotal_asset_Count && GrandTotal_asset_Count}
                          </span>
                          <br />

                          <span>{participating_percentage}%</span>
                        </>
                      );
                    })()}
                  </th>
                  <th colSpan={1} className="align-middle text-start"></th>
                  <th colSpan={1} className="align-middle text-start">
                    {(() => {
                      let stateWiseBidders = {};
                      let stateWiseSubscribedUsers = {};

                      sortedData.forEach((item) => {
                        const biddersCount =
                          Number(item?.participating_bidders_count) || 0;
                        const subscribedUsers =
                          Number(item?.subscribed_users_count) || 0;

                        if (!stateWiseBidders[item?.state]) {
                          stateWiseBidders[item?.state] = biddersCount;
                        }

                        if (!stateWiseSubscribedUsers[item?.state]) {
                          stateWiseSubscribedUsers[item?.state] =
                            subscribedUsers;
                        }
                      });
                      const GrandTotal_participating_bidders_count =
                        Object.values(stateWiseBidders).reduce(
                          (acc, count) => acc + count,
                          0,
                        );

                      const GrandTotal_subscribed_users_count = Object.values(
                        stateWiseSubscribedUsers,
                      ).reduce((acc, count) => acc + count, 0);

                      const participating_percentage =
                        GrandTotal_subscribed_users_count > 0
                          ? (
                              (GrandTotal_participating_bidders_count /
                                GrandTotal_subscribed_users_count) *
                              100
                            ).toFixed(2)
                          : 0;

                      return (
                        <>
                          <span>
                            {" "}
                            {GrandTotal_participating_bidders_count} /{" "}
                            {GrandTotal_subscribed_users_count}{" "}
                          </span>
                          <br />
                          <span>{participating_percentage}%</span>
                        </>
                      );
                    })()}
                  </th>
                  <th className="align-middle text-start"></th>
                  <th className="align-middle text-start"></th>

                  <th className="align-middle text-start">
                    {(() => {
                      let total_auctions = 0;
                      const total_participating_percentage = sortedData.reduce(
                        (acc, item) => {
                          if (Number(item?.participating_percentage) > 0) {
                            total_auctions += 1;
                            acc += Number(item?.participating_percentage) || 0;
                          }
                          return acc;
                        },
                        0,
                      );
                      const avg_participating_percentage = (
                        total_auctions > 0
                          ? total_participating_percentage / total_auctions
                          : 0
                      ).toFixed(2);
                      return `Avg. of percentage: ${avg_participating_percentage}%`;
                    })()}
                  </th>
                </tr>
              </tfoot>
            )}
            {!loading &&
              sortedData?.length > 0 &&
              flag === "Completed_OverView" && (
                <tfoot>
                  <tr>
                    <th colSpan={7}>Total</th>

                    <th colSpan={1} className="align-middle text-start">
                      {(() => {
                        const GrandTotal_asset_Count = sortedData.reduce(
                          (acc, item) => {
                            const assetCount = Number(item?.asset_count) || 0;
                            return acc + assetCount;
                          },
                          0,
                        );

                        const GrandTotal_asset_bidded_count = sortedData.reduce(
                          (acc, item) => {
                            const biddersCount =
                              Number(item?.bidded_asset_count) || 0;
                            return acc + biddersCount;
                          },
                          0,
                        );
                        const participating_percentage =
                          GrandTotal_asset_Count > 0
                            ? (
                                (GrandTotal_asset_bidded_count /
                                  GrandTotal_asset_Count) *
                                100
                              ).toFixed(2)
                            : 0;
                        return (
                          <>
                            <span>
                              {" "}
                              {GrandTotal_asset_bidded_count} /{" "}
                              {GrandTotal_asset_Count && GrandTotal_asset_Count}
                            </span>
                            <br />

                            <span>{participating_percentage}%</span>
                          </>
                        );
                      })()}
                    </th>
                    <th colSpan={1} className="align-middle text-start"></th>
                    <th colSpan={1} className="align-middle text-start">
                      {(() => {
                        let stateWiseBidders = {};
                        let stateWiseSubscribedUsers = {};

                        sortedData.forEach((item) => {
                          const biddersCount =
                            Number(item?.participating_bidders_count) || 0;
                          const subscribedUsers =
                            Number(item?.subscribed_users_count) || 0;

                          if (!stateWiseBidders[item?.state]) {
                            stateWiseBidders[item?.state] = biddersCount;
                          }

                          if (!stateWiseSubscribedUsers[item?.state]) {
                            stateWiseSubscribedUsers[item?.state] =
                              subscribedUsers;
                          }
                        });

                        const GrandTotal_subscribed_users_count = Object.values(
                          stateWiseSubscribedUsers,
                        ).reduce((acc, count) => acc + count, 0);
                        const GrandTotal_participating_bidders_count =
                          Object.values(stateWiseBidders).reduce(
                            (acc, count) => acc + count,
                            0,
                          );

                        const participating_percentage =
                          GrandTotal_subscribed_users_count > 0
                            ? (
                                (GrandTotal_participating_bidders_count /
                                  GrandTotal_subscribed_users_count) *
                                100
                              ).toFixed(2)
                            : 0;
                        return (
                          <>
                            <span>
                              {" "}
                              {GrandTotal_participating_bidders_count} /{" "}
                              {GrandTotal_subscribed_users_count}{" "}
                            </span>
                            <br />
                            <span>{participating_percentage}%</span>
                          </>
                        );
                      })()}
                    </th>

                    <th colSpan={1} className="align-middle text-start"></th>
                    <th colSpan={1} className="align-middle text-start"></th>
                    <th className="align-middle text-start">
                      {(() => {
                        let total_auctions = 0;
                        const total_participating_percentage =
                          sortedData.reduce((acc, item) => {
                            if (Number(item?.participating_percentage) > 0) {
                              total_auctions += 1;
                              acc +=
                                Number(item?.participating_percentage) || 0;
                            }
                            return acc;
                          }, 0);
                        const avg_participating_percentage = (
                          total_auctions > 0
                            ? total_participating_percentage / total_auctions
                            : 0
                        ).toFixed(2);
                        return `Avg. of percentage: ${avg_participating_percentage}%`;
                      })()}
                    </th>
                    <th colSpan={1} className="align-middle text-start">
                      {sortedData.reduce((acc, item) => {
                        const approvedCount =
                          Number(item?.approved_count) || 0;
                        return acc + approvedCount;
                      }, 0)}
                    </th>
                  </tr>
                </tfoot>
              )}
            {!loading && sortedData?.length > 0 && flag === "rc_challan" && (
              <tfoot>
                <tr>
                  <td></td>
                  <td className="align-middle text-start fw-semibold">Total</td>
                  <td></td>
                  <td></td>
                  <td className="align-middle text-start fw-semibold">
                    {(() => {
                      const GrandTotal_asset_Count = sortedData.reduce(
                        (acc, item) => {
                          const assetCount =
                            Number(item?.total_assets_viewed) || 0;
                          return acc + assetCount;
                        },
                        0,
                      );
                      return GrandTotal_asset_Count;
                    })()}
                  </td>
                </tr>
              </tfoot>
            )}
            {tfoot && sortedData?.length > 0 && !loading ? (
              <tfoot className="table-footer">{tfoot()}</tfoot>
            ) : null}
          </table>

          {!loading && enablePagination && sortedData?.length > 0 && (
            <div className={styles.paginationWrap}>
              {!hidePaginationInfo && (
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <span className={styles.paginationInfo}>
                    Showing{" "}
                    <strong>{(currentPage - 1) * rowsPerPage + 1}</strong> to{" "}
                    <strong>
                      {Math.min(currentPage * rowsPerPage, totalRows)}
                    </strong>{" "}
                    of <strong>{totalRows}</strong> entries
                  </span>
                  <label className={styles.rowsPerPage}>
                    Rows per page:
                    {rowsPerPageAsInput ? (
                      <input
                        type="number"
                        min={1}
                        value={rowsPerPageInputValue}
                        onChange={(e) => setRowsPerPageInputValue(e.target.value)}
                        onBlur={commitRowsPerPageInput}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitRowsPerPageInput();
                          }
                        }}
                        style={{ width: 64, marginLeft: 6 }}
                      />
                    ) : (
                      <select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                      >
                        {rowsPerPageOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                </div>
              )}

              <nav aria-label="Table pagination">
                <ul className={styles.pagination}>
                  {!hideFirstLastButtons && (
                    <li>
                      <button
                        type="button"
                        className={styles.pageBtn}
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        aria-label="First page"
                      >
                        <i className="bi bi-chevron-bar-left" />
                      </button>
                    </li>
                  )}
                  <li>
                    <button
                      type="button"
                      className={styles.pageBtn}
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                  </li>

                  {getPageNumbers.map((page, index) =>
                    page === "..." ? (
                      <li key={`ellipsis-${index}`}>
                        <span className={styles.pageEllipsis}>…</span>
                      </li>
                    ) : (
                      <li key={page}>
                        <button
                          type="button"
                          className={`${styles.pageBtn}${currentPage === page ? ` ${styles.isActive}` : ""}`}
                          onClick={() => handlePageChange(page)}
                          aria-label={`Go to page ${page}`}
                          aria-current={
                            currentPage === page ? "page" : undefined
                          }
                        >
                          {page}
                        </button>
                      </li>
                    ),
                  )}

                  <li>
                    <button
                      type="button"
                      className={styles.pageBtn}
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </li>
                  {!hideFirstLastButtons && (
                    <li>
                      <button
                        type="button"
                        className={styles.pageBtn}
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Last page"
                      >
                        <i className="bi bi-chevron-bar-right" />
                      </button>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          )}

          {!loading && filteredData.length === 0 && (
            <div className={styles.empty}>{no_data_msg}</div>
          )}
        </div>
      </>
    );
  } catch (error) {
    console.error(error);
    return <ErrorHandler error={error} />;
  }
}

export default memo(Table);
